import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Surface, formatINR } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, HardHat, Wallet, IndianRupee, ArrowRight, ArrowLeft, RotateCcw, Download } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/datetime";

const asNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => formatINR(n ?? 0);
const fmtDT = (iso) => formatDateTime(iso) || "—";

// CSV helpers (shared with WorkerLedgerDetail)
export const csvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export const downloadCSV = (content, filename) => {
  const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export default function WorkerLedger() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [payingWorker, setPayingWorker] = useState(null);  // worker for new payment

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/production/workers-balance");
      setRows(r.data.items); setTotals(r.data.totals);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Client-side search on the loaded snapshot
  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) =>
      (r.worker_name || "").toLowerCase().includes(s) ||
      (r.mobile || "").toLowerCase().includes(s) ||
      (r.worker_type || "").toLowerCase().includes(s),
    );
  }, [rows, search]);

  const exportCSV = () => {
    const header = ["Worker", "Mobile", "Type", "Total Earned", "Total Paid", "Total Reversed", "Net Payable", "Last Activity"];
    const lines = [header.join(",")];
    for (const r of filtered) {
      lines.push([
        csvCell(r.worker_name),
        csvCell(r.mobile || ""),
        csvCell(r.worker_type || ""),
        r.total_earned ?? 0,
        r.total_paid ?? 0,
        r.total_reversed ?? 0,
        r.net_payable ?? 0,
        csvCell(r.last_entry_at || ""),
      ].join(","));
    }
    downloadCSV(lines.join("\n"), `worker-ledger-summary-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${filtered.length} rows`);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-3">
        <Tile label="Workers" value={totals.total_workers || 0} Icon={HardHat} tone="gray" />
        <Tile label="Total Earned" value={fmt(totals.total_earned)} Icon={IndianRupee} tone="emerald" money />
        <Tile label="Total Paid" value={fmt(totals.total_paid)} Icon={Wallet} tone="blue" money />
        <Tile label="Net Payable" value={fmt(totals.net_payable)} Icon={IndianRupee} tone="red" money />
      </div>

      <div className="flex items-center gap-2">
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input placeholder="Search worker by name or mobile…" className="pl-8 h-9 text-sm bg-white" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="wl-search" />
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1.5" disabled={filtered.length === 0} data-testid="wl-export-csv">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
        <span className="ml-auto text-xs text-gray-500">{filtered.length} worker{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      <Surface padded={false}>
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2.5">Worker</th>
                <th className="px-3 py-2.5">Mobile</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5 text-right">Earned</th>
                <th className="px-3 py-2.5 text-right">Paid</th>
                <th className="px-3 py-2.5 text-right">Net Payable</th>
                <th className="px-3 py-2.5">Last Activity</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={8} className="text-center py-8 text-gray-400">Loading…</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-gray-400">
                  <HardHat className="h-8 w-8 mx-auto mb-2 opacity-40" />No worker ledger activity yet
                </td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.worker_id} className="hover:bg-gray-50" data-testid={`wl-row-${r.worker_id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900">{r.worker_name}</td>
                  <td className="px-3 py-2 text-gray-600">{r.mobile || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${r.worker_type === "Commission" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>{r.worker_type || "—"}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-700 tabular-nums">{fmt(r.total_earned)}</td>
                  <td className="px-3 py-2 text-right text-blue-700 tabular-nums">{fmt(r.total_paid)}</td>
                  <td className={`px-3 py-2 text-right font-bold tabular-nums ${r.net_payable > 0 ? "text-red-700" : r.net_payable < 0 ? "text-amber-700" : "text-gray-500"}`}>
                    {fmt(r.net_payable)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDT(r.last_entry_at)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => navigate(`/production/worker-ledger/${r.worker_id}`)} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-gray-100 text-xs text-gray-700" data-testid={`wl-view-${r.worker_id}`}>
                      Ledger <ArrowRight className="h-3 w-3" />
                    </button>
                    <button onClick={() => setPayingWorker(r)} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-emerald-50 text-xs text-emerald-700 font-semibold" data-testid={`wl-pay-${r.worker_id}`}>
                      <Wallet className="h-3.5 w-3.5" /> Pay
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      {payingWorker && <PaymentModal worker={payingWorker} onClose={() => setPayingWorker(null)} onSaved={() => { setPayingWorker(null); load(); }} />}
    </div>
  );
}

function PaymentModal({ worker, onClose, onSaved }) {
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    amount: "",
    payment_mode: "Cash",
    reference_no: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (asNum(form.amount) <= 0) { toast.error("Amount must be > 0"); return; }
    setSaving(true);
    try {
      const r = await api.post("/production/worker-payments", {
        payment_date: form.payment_date,
        worker_id: worker.worker_id,
        amount: asNum(form.amount),
        payment_mode: form.payment_mode,
        reference_no: form.reference_no.trim() || null,
        notes: form.notes.trim() || null,
      });
      toast.success(`${r.data.payment_no} recorded · balance: ${fmt(r.data.balance_after)}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Payment — {worker.worker_name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Payment Date *">
            <input type="date" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)}
              className="h-9 w-full rounded-md border border-gray-200 px-2.5 text-sm" data-testid="pay-date" />
          </F>
          <F label="Amount (₹) *">
            <input type="text" inputMode="decimal" value={form.amount}
              onChange={(e) => { const v = e.target.value; if (v === "" || /^\d*\.?\d*$/.test(v)) set("amount", v); }}
              placeholder="e.g. 500"
              className="h-9 w-full rounded-md border border-gray-200 px-2.5 text-sm text-right tabular-nums"
              data-testid="pay-amount" />
          </F>
          <F label="Payment Mode">
            <Select value={form.payment_mode} onValueChange={(v) => set("payment_mode", v)}>
              <SelectTrigger className="h-9 text-sm" data-testid="pay-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="Bank">Bank Transfer</SelectItem>
                <SelectItem value="UPI">UPI</SelectItem>
                <SelectItem value="Cheque">Cheque</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <F label="Reference # (Cheque/UPI ref)"><Input value={form.reference_no} onChange={(e) => set("reference_no", e.target.value)} data-testid="pay-ref" className="h-9" /></F>
          <F label="Notes" className="col-span-2">
            <textarea className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 min-h-[60px]" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="pay-notes" />
          </F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={saving} data-testid="pay-save">
            {saving ? "Recording…" : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Tile({ label, value, tone, Icon, money, highlight }) {
  const tones = {
    gray: "bg-white border-gray-200 text-gray-900",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-800",
    blue: "bg-blue-50 border-blue-100 text-blue-800",
    amber: "bg-amber-50 border-amber-100 text-amber-800",
    red: "bg-red-50 border-red-100 text-red-800",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[tone]} ${highlight ? "ring-2 ring-red-200" : ""}`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-widest opacity-70">{label}</p>
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
      </div>
      <p className={`mt-1 font-heading font-bold tabular-nums ${money ? "text-xl" : "text-2xl"}`}>{value}</p>
    </div>
  );
}

function F({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
